/**
 * Google reCAPTCHA v3 — token üretimi.
 *
 * v3 görünmezdir: kullanıcı hiçbir kutu işaretlemez, sayfada yalnızca sağ altta
 * Google'ın küçük "reCAPTCHA" rozeti görünür (Google'ın kullanım şartı bunu ya
 * da eşdeğer bir bilgilendirme metnini zorunlu kılar).
 *
 * KULLANIM:
 *     const token = await getRecaptchaToken("login");
 *     await api.post("/auth/login", { email, password, recaptcha_token: token });
 *
 * SİTE ANAHTARI `REACT_APP_RECAPTCHA_SITE_KEY` ile build'e girer. Tanımlı
 * değilse bu modül sessizce devre dışı kalır ve `null` döner → local
 * geliştirmede anahtar gerekmez. Backend de gizli anahtar yoksa doğrulamayı
 * atlar, böylece iki taraf tutarlı davranır.
 *
 * Script YALNIZCA BİR KEZ yüklenir. reCAPTCHA kullanan sayfalar mount olurken
 * `preloadRecaptcha()` çağırır → hem Google'ın rozeti sayfada görünür (kullanım
 * şartı), hem de gönderim anında script yüklemesi beklenmez.
 */
const SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "").trim();
const SCRIPT_ID = "google-recaptcha-v3";

let loaderPromise = null;

export function isRecaptchaConfigured() {
  return !!SITE_KEY;
}

function loadScript() {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.execute) {
      resolve(window.grecaptcha);
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    const el = existing || document.createElement("script");
    const onLoad = () => {
      if (!window.grecaptcha) {
        reject(new Error("grecaptcha yüklenemedi"));
        return;
      }
      // `ready` API hazır olana kadar bekler; execute'u ondan önce çağırmak hata verir.
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };
    el.addEventListener("load", onLoad, { once: true });
    el.addEventListener(
      "error",
      () => {
        // Yeniden denenebilsin diye önbelleği temizle.
        loaderPromise = null;
        reject(new Error("reCAPTCHA script yüklenemedi"));
      },
      { once: true }
    );
    if (!existing) {
      el.id = SCRIPT_ID;
      el.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
      el.async = true;
      el.defer = true;
      document.head.appendChild(el);
    }
  });
  return loaderPromise;
}

/**
 * Script'i şimdiden yükler (rozet görünür olsun + gönderim anı hızlı olsun).
 * Hata fırlatmaz; anahtar yoksa hiçbir şey yapmaz.
 */
export function preloadRecaptcha() {
  if (!SITE_KEY) return;
  loadScript().catch(() => {
    /* getRecaptchaToken zaten yeniden dener ve null döner */
  });
}

/**
 * Verilen eylem için taze bir token döner.
 *
 * HİÇ HATA FIRLATMAZ: script yüklenemezse / Google erişilemezse `null` döner ve
 * form gönderimi engellenmez. Karar backend'e bırakılır (o da ağ hatasında
 * fail-open davranır) — aksi hâlde Google tarafındaki bir kesinti kullanıcıyı
 * sisteme giremez hâle getirirdi. Brute force koruması ayrıca zaten var.
 */
export async function getRecaptchaToken(action) {
  if (!SITE_KEY) return null;
  try {
    const grecaptcha = await loadScript();
    return await grecaptcha.execute(SITE_KEY, { action });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("reCAPTCHA token alınamadı:", e?.message || e);
    return null;
  }
}
